import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import ProducerReviewInlineDrawer from "../components/ProducerReviewInlineDrawer";

const STATUS_STYLES = {
  draft: "bg-muted text-ink2 border-line",
  pending_review: "bg-ink text-white border-ink",
  needs_info: "bg-danger/10 text-danger border-danger/40",
  approved: "bg-leaf/10 text-leaf border-leaf/40",
  rejected: "bg-danger/10 text-danger border-danger/40",
};

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function AdminProducers() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`admin_producers.${k}`, o);
  const STATUS_FILTERS = useMemo(() => [
    { id: "queue", label: tr("filter_queue", "In review") },
    { id: "pending_review", label: tr("filter_pending", "Pending review") },
    { id: "needs_info", label: tr("filter_needs_info", "Needs info") },
    { id: "approved", label: tr("filter_approved", "Approved") },
    { id: "rejected", label: tr("filter_rejected", "Rejected") },
  ], [t]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [filter, setFilter] = useState("queue");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "queue" ? {} : { status: filter };
      const { data } = await api.get("/admin/producers/queue", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || tr("load_failed", "Failed to load queue"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <div className="label-tag mb-4">{tr("tag", "Admin · Producer review")}</div>
        <h1 className="font-serif text-5xl tracking-tight">{tr("title", "Compliance queue")}</h1>
        <p className="text-ink2 mt-3 max-w-2xl text-sm">
          {tr("subtitle", "Review producer-submitted UCS listings. Approve to publish to the marketplace, request more info, or reject.")}
        </p>

        <div className="mt-6 flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => {
            const count = f.id === "queue"
              ? (counts.pending_review || 0) + (counts.needs_info || 0)
              : (counts[f.id] || 0);
            return (
              <button
                key={f.id}
                data-testid={`prod-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 text-[11px] font-mono uppercase tracking-wider border ${
                  filter === f.id ? "bg-ink text-white border-ink" : "border-line text-ink2 hover:border-ink"
                }`}
              >
                {f.label} · {count}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {loading && <div className="py-12 text-center text-ink2 font-mono text-sm">{tr("loading", "Loading…")}</div>}
        {!loading && items.length === 0 && (
          <div className="border border-line bg-surface p-10 text-center text-ink2 text-sm">{tr("nothing_here", "Nothing here.")}</div>
        )}
        {!loading && items.length > 0 && (
          <div className="border border-line bg-surface" data-testid="prod-queue-table">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[11px] font-mono uppercase tracking-wider text-ink2">
                <tr>
                  <th className="text-left p-3">{tr("col_listing", "Listing")}</th>
                  <th className="text-left p-3">{tr("col_producer", "Producer")}</th>
                  <th className="text-right p-3">{tr("col_volume", "Volume")}</th>
                  <th className="text-right p-3">{tr("col_price", "Price")}</th>
                  <th className="text-left p-3">{tr("col_status", "Status")}</th>
                  <th className="text-left p-3">{tr("col_updated", "Updated")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="border-t border-line hover:bg-bg/40 cursor-pointer"
                    onClick={() => setActive(it)}
                    data-testid={`prod-row-${it.id}`}
                  >
                    <td className="p-3">
                      <div>{it.name}</div>
                      <div className="text-[11px] font-mono text-ink2">{it.region} · {it.methodology}</div>
                    </td>
                    <td className="p-3">
                      <div>{it.producer_name}</div>
                      <div className="text-[11px] font-mono text-ink2">{it.producer_email}</div>
                    </td>
                    <td className="p-3 text-right font-mono">{Number(it.tons_available || 0).toLocaleString()} t</td>
                    <td className="p-3 text-right font-mono">{fmtUsd(it.ask_price_usd_per_ton)}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-mono uppercase px-2 py-1 border ${STATUS_STYLES[it.status] || STATUS_STYLES.draft}`}>
                        {tr(`status_${it.status}`, it.status.replace("_", " "))}
                      </span>
                    </td>
                    <td className="p-3 text-[11px] font-mono text-ink2">
                      {new Date(it.updated_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {active && (
        <ProducerReviewInlineDrawer
          listing={active}
          onClose={() => setActive(null)}
          onChanged={() => { setActive(null); load(); }}
        />
      )}
    </Layout>
  );
}
